/**
 * Viewport-aware thumbnail prefetch.
 *
 * The eager `useThumbnailPrefetch` hook fetches every slide in the list
 * upfront — fine for small folders, painful when there are many slides
 * (or many collapsed decks) and the user only looks at a fraction of them.
 *
 * `useSlideThumbnailLazyRequest` attaches an IntersectionObserver to a
 * card and queues a single thumbnail request when the card is within
 * ~1000 px of the viewport. The queue is debounced so a burst of
 * intersections (e.g. unfolding a deck, fast scroll) collapses into one
 * batched HTTP call — preserving the EasyAuth amortization the original
 * batch endpoint was built for.
 */

import { useEffect, useRef } from "react"

import { prefetchSlideThumbnails, useThumbnailCache } from "./use-thumbnail-prefetch"

const FLUSH_DEBOUNCE_MS = 50
const VIEWPORT_MARGIN = "1000px"

const pendingIds = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

function scheduleFlush() {
  if (flushTimer !== null) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    const ids = Array.from(pendingIds)
    pendingIds.clear()
    if (ids.length > 0) void prefetchSlideThumbnails(ids)
  }, FLUSH_DEBOUNCE_MS)
}

/**
 * Queue a single thumbnail id for the next batched fetch.
 *
 * Exported so non-IO callers (e.g. `useSlideThumbnail`'s mount-time
 * fallback when the cache is empty) can join the same 50 ms debounce
 * window instead of each firing a single-id batch request. A burst of
 * N SlideCards mounting in the same tick coalesces into one HTTP call
 * with N ids.
 *
 * No-op when the slide is already cached or in-flight.
 */
export function requestThumbnail(slideId: string) {
  const cache = useThumbnailCache.getState()
  if (cache.has(slideId) || cache.isPending(slideId)) return
  pendingIds.add(slideId)
  scheduleFlush()
}

export function useSlideThumbnailLazyRequest(slideId: string | undefined) {
  const ref = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!slideId) return
    const el = ref.current
    if (!el) return
    if (useThumbnailCache.getState().has(slideId)) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            requestThumbnail(slideId)
            observer.disconnect()
            break
          }
        }
      },
      { rootMargin: VIEWPORT_MARGIN }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [slideId])
  return ref
}
